import React, { useCallback, useMemo, useState } from "react";

import { formAtom } from "@impero-com/common/utils/form/formAtom";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { atom } from "jotai";
import * as Yup from "yup";

import {
    InitialFieldType,
    SubsequentFieldType,
    SavedBlob,
} from "api/generated/model";
import { FieldBuilder } from "components/fieldBuilder/fieldBuilder";
import { Field } from "components/fieldBuilder/models";
import {
    FieldPlaceholderCmp,
    renderPlaceholder,
} from "components/fieldBuilder/utils";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { useOrganizationCurrenciesQuery } from "../../queries/useOrganizationCurrenciesQuery";

import {
    ControlField,
    ControlFieldType,
    ControlFieldTypeName,
    createNewControlField,
    getControlFieldTypeConditionalValues,
    getControlFieldTypeDrawerBehavior,
    getFieldTypeInfo,
    getNewTaskDefaultCurrency,
} from "./fieldBuilderUtils";
import { ControlFieldCmp } from "./FieldCmp";
import { FieldCustomSection } from "./FieldCustomSection";
import { ControlFieldDrawerHeader } from "./FieldDrawerHeader";
import {
    DataSheetAssignmentsForControlProgramAndCurrentControl,
    FieldTypeName,
} from "./types";

const allFieldTypeNames: Array<ControlFieldTypeName> = [
    "TextInput",
    "TextArea",
    "NumericInput",
    "MoneyInput",
    "CheckboxGroup",
    "RadioButtonGroup",
    "Dropdown",
    "FileUpload",
    "DataSheet",
    "DataGrid",
    "Heading",
];

/**
 * A new Money task starts on the organization's default currency, so the picker offers it only once
 * the currencies have loaded.
 */
const useTaskPicker = (displayDataSheetButton: boolean) => {
    const moneyTaskTypeEnabled = useFeatureFlag("moneyTaskType");
    const { data: currencies = [] } = useOrganizationCurrenciesQuery({
        enabled: moneyTaskTypeEnabled,
    });
    const defaultCurrency = moneyTaskTypeEnabled
        ? getNewTaskDefaultCurrency(currencies)
        : undefined;

    const shownFieldTypeNames = allFieldTypeNames.filter((fieldTypeName) => {
        if (fieldTypeName === "DataSheet") {
            return displayDataSheetButton;
        } else if (fieldTypeName === "MoneyInput") {
            return defaultCurrency !== undefined;
        } else {
            return true;
        }
    });
    const fieldButtons = shownFieldTypeNames.map((fieldTypeName) => {
        const info = getFieldTypeInfo()(fieldTypeName);
        return {
            id: fieldTypeName,
            icon: {
                svg: info.svg,
            },
            label: info.label,
            renderPlaceholder: () => renderPlaceholder(info),
        };
    });

    const createNewField = useCallback(
        (fieldTypeName: ControlFieldTypeName, fieldType?: ControlFieldType) =>
            createNewControlField(fieldTypeName, fieldType, defaultCurrency),
        [defaultCurrency]
    );

    return { fieldButtons, createNewField };
};

export const useBlobsFormAtom = (blobs: SavedBlob[]) => {
    const [blobsAtom] = useState(() => atom<Array<SavedBlob>>(blobs));
    const blobsFormAtom = useMemo(
        () => formAtom(blobsAtom, Yup.mixed<Array<SavedBlob>>().defined()),
        [blobsAtom]
    );
    return blobsFormAtom;
};

interface ControlTasksProps<
    T extends
        | FormAtom<Field<InitialFieldType, string>[]>
        | FormAtom<Field<SubsequentFieldType, string>[]>,
> {
    disabled: boolean;
    atom: T;
    dataSheetAssignments: DataSheetAssignmentsForControlProgramAndCurrentControl;
    controlId: string;
    controlTitle: string;
    blobs: SavedBlob[];
    headerAction?: React.ReactNode;
}

export const ControlInitialTasks = ({
    atom: fieldsAtom,
    blobs,
    dataSheetAssignments,
    controlId,
    controlTitle,
    disabled,
    headerAction,
}: ControlTasksProps<FormAtom<Field<InitialFieldType, string>[]>>) => {
    const { t } = useTranslation();
    const displayDataSheetButton =
        dataSheetAssignments.linkedResources.dataSheets.length > 0;
    const { fieldButtons, createNewField } = useTaskPicker(
        displayDataSheetButton
    );
    const blobsFormAtom = useBlobsFormAtom(blobs);

    return (
        <FieldBuilder<
            Field<InitialFieldType, string>,
            InitialFieldType,
            FieldTypeName<InitialFieldType>
        >
            contentLabel={t("CreateControl_Tasks_Field_Title")}
            buttonPanelLabel={t("CreateControl_Tasks_Elements")}
            headerAction={headerAction}
            noElementsLabel={t("CreateControl_Tasks_NoElements")}
            fieldButtons={fieldButtons}
            fieldsAtom={fieldsAtom}
            renderField={(fa, openDrawer) => (
                <ControlFieldCmp
                    fieldsAtom={fieldsAtom}
                    fieldAtom={fa}
                    disabled={disabled}
                    openDrawer={openDrawer}
                />
            )}
            renderFieldPlaceholder={(_fa) => <FieldPlaceholderCmp />}
            renderFieldDrawerHeader={(fa) => (
                <ControlFieldDrawerHeader fieldAtom={fa} />
            )}
            renderFieldDrawerCustomSection={(fa, ref) => (
                <FieldCustomSection
                    ref={ref}
                    fieldAtom={fa}
                    dataSheetAssignments={dataSheetAssignments}
                    controlOverrideId={controlId}
                    controlOverrideTitle={controlTitle}
                    disabled={disabled}
                />
            )}
            getFieldTypeConditionalValues={getControlFieldTypeConditionalValues}
            getFieldTypeDrawerBehavior={getControlFieldTypeDrawerBehavior}
            disabled={disabled}
            createNewField={createNewField}
            blobsAtom={blobsFormAtom}
        />
    );
};

export const ControlSubsequentTasks = ({
    atom: fieldsAtom,
    blobs,
    dataSheetAssignments,
    controlId,
    controlTitle,
    disabled,
    headerAction,
}: ControlTasksProps<FormAtom<Field<SubsequentFieldType, string>[]>>) => {
    const { t } = useTranslation();

    // Subsequent steps have no datasheet fields
    const displayDataSheetButton = false;
    const { fieldButtons, createNewField } = useTaskPicker(
        displayDataSheetButton
    );
    const blobsFormAtom = useBlobsFormAtom(blobs);

    // Ideally we would use the SubsequentStep types, but it seemed it would require significant refactors in FieldBuilder
    return (
        <FieldBuilder<ControlField, ControlFieldType, ControlFieldTypeName>
            contentLabel={t("CreateControl_Tasks_Field_Title")}
            buttonPanelLabel={t("CreateControl_Tasks_Elements")}
            headerAction={headerAction}
            noElementsLabel={t("CreateControl_Tasks_NoElements")}
            fieldButtons={fieldButtons}
            fieldsAtom={fieldsAtom as FormAtom<ControlField[]>}
            renderField={(fa, openDrawer) => (
                <ControlFieldCmp
                    fieldsAtom={fieldsAtom}
                    fieldAtom={fa}
                    disabled={disabled}
                    openDrawer={openDrawer}
                />
            )}
            renderFieldPlaceholder={(_fa) => <FieldPlaceholderCmp />}
            renderFieldDrawerHeader={(fa) => (
                <ControlFieldDrawerHeader fieldAtom={fa} />
            )}
            renderFieldDrawerCustomSection={(fa, ref) => (
                <FieldCustomSection
                    ref={ref}
                    fieldAtom={fa}
                    dataSheetAssignments={dataSheetAssignments}
                    controlOverrideId={controlId}
                    controlOverrideTitle={controlTitle}
                    disabled={disabled}
                />
            )}
            getFieldTypeConditionalValues={getControlFieldTypeConditionalValues}
            getFieldTypeDrawerBehavior={getControlFieldTypeDrawerBehavior}
            disabled={disabled}
            createNewField={createNewField}
            blobsAtom={blobsFormAtom}
        />
    );
};
